'use client';

import { useState, useEffect, useCallback } from 'react';
import { usesConfigurator } from '@/core/utils/product-action';
import dynamic from 'next/dynamic';
import AddToCartButton from './add-to-cart-button';
import { B2BTierList } from './B2BTierList';
import { b2bTierPrice, type B2BTier } from '@/core/utils/b2b-pricing';
import { SubscriptionPurchasePanel } from './subscription-purchase-panel';
import { activeSubscriptionTiers } from '@/core/types/subscription-tiers';
import { LeaseNowProductBadge } from './LeaseNowProductBadge';
import { Play, Calendar, Clock, MapPin, ShoppingCart, ShoppingBag } from 'lucide-react';
import { Label } from '@/core/components/ui/label';
import { Input } from '@/core/components/ui/input';
import { Textarea } from '@/core/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/core/components/ui/select';
import { fetchAPI } from '@/core/api/client';
import { toast } from '@/core/utils/toast';
import { useTranslation } from '@/core/components/providers/TranslationProvider';
import Image from 'next/image';
import { getAbsoluteMediaUrl, getPublicMediaUrl, toOptimizableSrc } from '@/core/utils/media';
import { useSession } from 'next-auth/react';
import QuickOrderMatrix from './QuickOrderMatrix';
import { cn } from '@/core/utils/cn';
import SeatMapPicker from './seat-map-picker';
import { trackEvent, trackAddToCart } from '@/core/utils/analytics';
import { safeSanitize } from '@/core/utils/sanitize';
import { getOrCreateSeatSessionToken } from '@/lib/seat-map-utils';
import { useLocalizeHtml } from '@/core/i18n/navigation';

export function EventSelector({ product, settings }: { product: any; settings?: any }) {
    const { t, currency, locale } = useTranslation();
    const s = settings || {};
    const [events, setEvents] = useState<any[]>([]);
    const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
    const [selectedSection, setSelectedSection] = useState<string>('');
    const [quantity, setQuantity] = useState(1);
    const [loading, setLoading] = useState(true);
    const [added, setAdded] = useState(false);
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [sessionToken] = useState(() => getOrCreateSeatSessionToken());

    const load = useCallback(async () => {
        const data = await fetchAPI(`/products/${product.id}/events`, { next: { revalidate: 0 } });
        const list = data || [];
        setEvents(list);
        // Dates past their sales deadline stay listed (marked closed); preselect the first open one.
        const first = list.find((e: any) => e.sales_open !== false) || list[0];
        if (first) {
            setSelectedEventId(first.id);
            if (first.sections?.length > 0) setSelectedSection(first.sections[0].name);
        }
        setLoading(false);
    }, [product.id]);

    useEffect(() => { load(); }, [load]);

    const selectedEvent = events.find(e => e.id === selectedEventId);
    const salesClosed = selectedEvent?.sales_open === false;
    const hasSeatMap = !!selectedEvent?.seat_map;
    const formatDeadline = (iso: string) => new Date(iso).toLocaleString(locale || undefined, {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const sectionDef = selectedEvent?.sections?.find((sec: any) => sec.name === selectedSection);
    const price = sectionDef?.price ?? product.price ?? 0;
    // Registration fields (driver, car…): one cart line per signup, quantity fixed at 1.
    const formFields: any[] = !hasSeatMap ? (selectedEvent?.form_fields || []) : [];
    const hasForm = formFields.length > 0;
    const maxQty = hasForm ? 1 : Math.min(10, selectedEvent?.available_seats ?? 10);

    function handleEventChange(eventId: number) {
        setSelectedEventId(eventId);
        const ev = events.find(e => e.id === eventId);
        if (ev?.sections?.length > 0) setSelectedSection(ev.sections[0].name);
        setQuantity(1);
        setFormValues({});
    }

    function addToCart() {
        const mainImage = product.images?.find((img: any) => img.is_main) || product.images?.[0];
        const imageUrl = mainImage ? getPublicMediaUrl(mainImage.path) : null;
        const eventLabel = selectedEvent
            ? `${selectedEvent.event_date} ${selectedEvent.event_time?.slice(0, 5)}`
            : '';

        // ── General admission (no seat map) ───────────────────────────────────
        if (salesClosed) {
            toast.error(t('event.sales_closed_info'));
            return;
        }
        if (!selectedEventId || !selectedSection) {
            toast.error(t('event.select_required'));
            return;
        }
        const missing = formFields.find(f => f.required && !(formValues[f.key] || '').trim());
        if (missing) {
            toast.error(t('event.field_required').replace('{label}', missing.label));
            return;
        }
        const answers: Record<string, string> = {};
        const answerLabels: Record<string, string> = {};
        for (const f of formFields) {
            const value = (formValues[f.key] || '').trim();
            if (!value) continue;
            answers[f.key] = value;
            answerLabels[f.label] = value;
        }
        const cart = JSON.parse(localStorage.getItem('stag-cart') || '[]');
        const cartItemId = hasForm
            ? `${product.id}-event-${selectedEventId}-${selectedSection}-${Date.now()}`
            : `${product.id}-event-${selectedEventId}-${selectedSection}`;
        const existing = hasForm ? null : cart.find((i: any) => i.cartItemId === cartItemId);
        const ticketName = `${product.name} – ${eventLabel} (${selectedSection})`;
        if (existing) {
            existing.quantity = Math.min(existing.quantity + quantity, maxQty);
        } else {
            cart.push({
                cartItemId,
                id: product.id,
                variant_id: null,
                event_details_id: selectedEventId,
                ticket_section: selectedSection,
                name: ticketName,
                price,
                image: imageUrl,
                slug: product.slug,
                quantity,
                attributes: {
                    'Termin': eventLabel,
                    'Typ biletu': selectedSection,
                    ...answerLabels,
                },
                ...(hasForm ? { ticket_form: answers, max_quantity: 1 } : {}),
            });
        }
        setFormValues({});
        localStorage.setItem('stag-cart', JSON.stringify(cart));

        // Track GA4/Pixel AddToCart
        // Item id stays the product's feed id (the cart line id is per event/section, unknown to Google).
        trackAddToCart(product, null, quantity, currency || 'PLN', {
            customName: ticketName,
            customPrice: price
        });

        window.dispatchEvent(new Event('cart-updated'));
        toast.success(t('product.add_to_cart_success'));
        setAdded(true);
        setTimeout(() => setAdded(false), 2000);
    }

    if (loading) return <div className="py-6 text-slate-400 text-sm">{t('event.loading')}</div>;
    if (events.length === 0) return <div className="py-6 text-slate-500 font-medium">{t('event.no_events')}</div>;

    const eventLabel = selectedEvent
        ? `${selectedEvent.event_date} ${selectedEvent.event_time?.slice(0, 5)}`
        : '';
    const canAddToCart = !!selectedEventId && !!selectedSection && !salesClosed && (selectedEvent?.available_seats ?? 0) > 0;

    return (
        <div className="space-y-5 border-y border-slate-100 py-6">
            {/* Event date selector */}
            <div className="space-y-2">
                <Label className="text-sm font-bold text-slate-900 uppercase tracking-wider">{t('event.select_date')}</Label>
                <div className="space-y-2">
                    {events.map(ev => (
                        <button
                            key={ev.id}
                            type="button"
                            onClick={() => handleEventChange(ev.id)}
                            className="w-full flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 rounded-xl border text-sm transition-all text-left"
                            style={selectedEventId === ev.id ? {
                                backgroundColor: s.activeEventBg || 'rgba(147,51,234,0.05)',
                                borderColor: s.activeEventBorder || undefined,
                                outline: `2px solid ${s.activeEventRing || 'rgba(147,51,234,0.2)'}`,
                                outlineOffset: '0px',
                            } : {
                                backgroundColor: s.inactiveEventBg || '#ffffff',
                                borderColor: s.inactiveEventBorder || '#e2e8f0',
                            }}
                        >
                            <Calendar className="h-4 w-4 text-purple-500 shrink-0" />
                            <span className="font-semibold">{ev.event_date}</span>
                            <Clock className="h-4 w-4 text-purple-500 shrink-0" />
                            <span>{ev.event_time?.slice(0, 5)}</span>
                            <MapPin className="h-4 w-4 text-purple-500 shrink-0" />
                            <span className="text-slate-600">{ev.venue_name}</span>
                            <span className="ml-auto text-xs text-slate-400 shrink-0">
                                {ev.sales_open === false
                                    ? t('event.sales_closed')
                                    : s.hideSeatsLeft ? null : `${ev.available_seats} ${t('event.seats_available')}`}
                            </span>
                            {ev.sales_end_at && ev.sales_open !== false && (
                                <span className="basis-full text-xs text-slate-500">
                                    {t('event.sales_until').replace('{date}', formatDeadline(ev.sales_end_at))}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {salesClosed && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
                    {t('event.sales_closed_info')}
                </p>
            )}

            {/* Seat map (assigned seating) — manages its own "Add to cart" */}
            {hasSeatMap && selectedEventId && !salesClosed && (
                <SeatMapPicker
                    productId={product.id}
                    eventId={selectedEventId}
                    sessionToken={sessionToken}
                    product={product}
                    eventLabel={eventLabel}
                />
            )}

            {/* Section selector (general admission) */}
            {!hasSeatMap && !salesClosed && selectedEvent?.sections?.length > 0 && (
                <div className="space-y-2">
                    <Label className="text-sm font-bold text-slate-900 uppercase tracking-wider">{t('event.ticket_category')}</Label>
                    <div className="flex flex-wrap gap-2">
                        {selectedEvent.sections.map((sec: any) => (
                            <button
                                key={sec.name}
                                type="button"
                                onClick={() => setSelectedSection(sec.name)}
                                className="px-4 py-2 rounded-lg text-sm font-medium border transition-all shadow-sm"
                                style={selectedSection === sec.name ? {
                                    backgroundColor: s.activeSectionBg || '#0f172a',
                                    color: s.activeSectionText || '#ffffff',
                                    borderColor: s.activeSectionBg || '#0f172a',
                                } : {
                                    backgroundColor: s.inactiveSectionBg || '#ffffff',
                                    color: s.inactiveSectionText || '#475569',
                                    borderColor: '#e2e8f0',
                                }}
                            >
                                {sec.name} — {sec.price.toFixed(2)} {currency}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Registration fields (GA only) */}
            {hasForm && !salesClosed && (
                <div className="space-y-3">
                    <Label className="text-sm font-bold text-slate-900 uppercase tracking-wider">{t('event.registration_details')}</Label>
                    {formFields.map(f => {
                        const id = `ticket-field-${selectedEventId}-${f.key}`;
                        const value = formValues[f.key] || '';
                        const set = (v: string) => setFormValues(prev => ({ ...prev, [f.key]: v }));
                        return (
                            <div key={f.key} className="space-y-1">
                                <Label htmlFor={id} className="text-sm text-slate-700">
                                    {f.label}{f.required && <span className="text-red-500"> *</span>}
                                </Label>
                                {f.type === 'select' ? (
                                    <Select value={value || undefined} onValueChange={set}>
                                        <SelectTrigger id={id}><SelectValue placeholder={t('event.select_option')} /></SelectTrigger>
                                        <SelectContent>
                                            {(f.options || []).map((o: string) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                ) : f.type === 'textarea' ? (
                                    <Textarea id={id} value={value} maxLength={300} onChange={e => set(e.target.value)} />
                                ) : (
                                    <Input id={id} value={value} maxLength={300}
                                        type={f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : 'text'}
                                        inputMode={f.type === 'number' ? 'decimal' : undefined}
                                        onChange={e => set(e.target.value)} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Quantity (GA only) + price */}
            {!hasSeatMap && !salesClosed && (
                <div className="flex items-center gap-4 pt-2">
                    {!hasForm && (
                    <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden">
                        <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))} className="px-3 py-2 text-slate-600 hover:bg-slate-50 font-bold">−</button>
                        <span className="px-4 py-2 font-semibold text-slate-900 min-w-[3rem] text-center">{quantity}</span>
                        <button type="button" onClick={() => setQuantity(q => Math.min(maxQty, q + 1))} className="px-3 py-2 text-slate-600 hover:bg-slate-50 font-bold">+</button>
                    </div>
                    )}
                    <div>
                        <p className="text-2xl font-bold text-primary">{(price * quantity).toFixed(2)} {currency}</p>
                        {quantity > 1 && <p className="text-xs text-slate-500">{price.toFixed(2)} {currency} {t('event.per_unit')}</p>}
                    </div>
                </div>
            )}

            {/* GA "Add to cart" — seat-map mode uses SeatMapPicker's own button instead */}
            {!hasSeatMap && !salesClosed && (
                <button
                    type="button"
                    onClick={addToCart}
                    disabled={!canAddToCart}
                    className="flex items-center gap-2 px-8 py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 shadow-md"
                >
                    <ShoppingCart className="h-5 w-5" />
                    {added
                        ? t('product.added')
                        : (selectedEvent?.available_seats === 0 ? t('event.no_seats') : t('product.add_to_cart'))}
                </button>
            )}
        </div>
    );
}

import NettoBruttoToggle, { getPriceDisplayMode } from '../layout/NettoBruttoToggle';
import ProductAttachmentsBlock from './ProductAttachmentsBlock';
import { LoyaltyEarnPreview } from '@/core/components/loyalty/LoyaltyEarnPreview';
import { ProductProvider, useProductState } from '@/core/components/Builder/ProductContext';
// Statically importing this pulled all ~2000 lines of ProductBlocks.tsx (14 product-builder
// block components) into this fallback PDP's client bundle just for the configurator variant
// picker — dynamic-import it, same as blockRegistry.ts does for the builder-block path.
const ProductVariantsBlock = dynamic(() => import('@/core/components/Builder/blocks/ProductBlocks').then(m => m.ProductVariantsBlock), {
    loading: () => <div className="w-full min-h-[120px] animate-pulse bg-gray-50 rounded-xl" />,
});

export function ProductDetails({ product }: { product: any }) {
    return (
        <ProductProvider product={product}>
            <ProductDetailsInner product={product} />
        </ProductProvider>
    );
}

function ProductDetailsInner({ product }: { product: any }) {
  const localizeHtml = useLocalizeHtml();
    const { t, currency } = useTranslation();
    const { data: session } = useSession();
    const isB2B = (session?.user as any)?.type === 'b2b';
    const isTicket = product.product_type === 'ticket';
    // A configurable product, or a D2D service with options (pranie / + prasowanie, ekspres…).
    const isConfigurable = usesConfigurator(product);
    const images = product.images || [];
    const variants = product.variants || [];
    const attachments = product.attachments || [];
    const isVariable = product.product_type === 'variable';
    const isSubscriptionPlan = product.product_type === 'subscription_plan';
    // A `service`'s own "subscribe and save" tiers — its normal one-off purchase (AddToCartButton
    // below) stays available alongside this, unlike a pure subscription_plan which has nothing
    // else to buy. See the shared modules/shop/components/product/subscription-purchase-panel.tsx.
    const subscriptionTiers = product.product_type === 'service' ? activeSubscriptionTiers(product.service_config) : [];

    const [priceMode, setPriceMode] = useState<'netto' | 'brutto'>('brutto');

    useEffect(() => {
        setPriceMode(getPriceDisplayMode(isB2B ? 'netto' : 'brutto'));
        const handleModeChange = (e: any) => {
            if (e.detail === 'netto' || e.detail === 'brutto') {
                setPriceMode(e.detail);
            }
        };
        window.addEventListener('stag-price-mode-change', handleModeChange);
        return () => window.removeEventListener('stag-price-mode-change', handleModeChange);
    }, [isB2B]);

    const sortedImages = [...images].sort((a: any, b: any) => {
        if (a.is_main && !b.is_main) return -1;
        if (!a.is_main && b.is_main) return 1;
        return (a.order || 0) - (b.order || 0);
    });

    const [selectedIndex, setSelectedIndex] = useState(0);
    const [selectedAttributes, setSelectedAttributes] = useState<Record<string, string>>({});
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const selectedMedia = sortedImages[selectedIndex] || null;

    const getMediaUrl = (path: string) => {
        return toOptimizableSrc(getAbsoluteMediaUrl(path));
    };

    // Find current variant based on selection
    const currentVariant = isVariable ? variants.find((v: any) => {
        return Object.entries(selectedAttributes).every(([key, val]) => v.attributes[key] === val) &&
               Object.keys(v.attributes).length === Object.keys(selectedAttributes).length;
    }) : null;

    // Fallback to default variant or first variant if none selected yet
    const displayVariant = currentVariant || (isVariable ? variants.find((v: any) => v.is_default) || variants[0] : null);

    // Auto-switch main image if variant has a specific image
    useEffect(() => {
        if (displayVariant?.image_path) {
            const idx = sortedImages.findIndex((img: any) => img.path === displayVariant.image_path);
            if (idx >= 0) setSelectedIndex(idx);
        }
    }, [displayVariant, sortedImages]);

    // Get all unique attributes and their possible values from variants
    const attributesMap: Record<string, Set<string>> = {};
    variants.forEach((v: any) => {
        Object.entries(v.attributes).forEach(([key, val]: [string, any]) => {
            if (!attributesMap[key]) attributesMap[key] = new Set();
            attributesMap[key].add(val);
        });
    });

    // Initial attribute selection (from default variant)
    const handleAttributeSelect = (attrName: string, val: string) => {
        const newSelection = { ...selectedAttributes, [attrName]: val };
        
        // 1. Check if a perfect variant matches this combination
        const perfectVariantExists = variants.some((v: any) => {
            return Object.entries(newSelection).every(([k, sVal]) => v.attributes[k] === sVal) &&
                   Object.keys(v.attributes).length === Object.keys(newSelection).length;
        });
        
        if (perfectVariantExists) {
            setSelectedAttributes(newSelection);
            return;
        }
        
        // 2. Find any variant containing [attrName]: val
        const possibleVariants = variants.filter((v: any) => v.attributes[attrName] === val);
        if (possibleVariants.length > 0) {
            // Find one that shares the most selected attributes
            let bestVariant = possibleVariants[0];
            let maxMatches = -1;
            for (const v of possibleVariants) {
                let matches = 0;
                for (const [k, sVal] of Object.entries(newSelection)) {
                    if (v.attributes[k] === sVal) {
                        matches++;
                    }
                }
                if (matches > maxMatches) {
                    maxMatches = matches;
                    bestVariant = v;
                }
            }
            setSelectedAttributes(bestVariant.attributes);
        } else {
            setSelectedAttributes(newSelection);
        }
    };

    useEffect(() => {
        if (isVariable && Object.keys(selectedAttributes).length === 0) {
            const defVariant = variants.find((v: any) => v.is_default) || variants[0];
            if (defVariant) {
                setSelectedAttributes(defVariant.attributes);
            }
        }
    }, [isVariable, variants]);

    const productContext = useProductState();
    const configuratorPrice = productContext?.configuratorPrice;

    // Progi ilościowe zalogowanej firmy (D9) — cena dla wybranej ilości, jak w koszyku i zamówieniu.
    const b2bTiers: B2BTier[] = isConfigurable ? [] : ((displayVariant || product).b2b_tiers || []);
    const baseActivePrice = b2bTierPrice(displayVariant ? displayVariant.price : product.price, b2bTiers, selectedQuantity);
    const activePrice = isConfigurable && configuratorPrice !== undefined && configuratorPrice !== null
        ? configuratorPrice
        : baseActivePrice;
    const activeSalesPrice = isConfigurable ? null : (displayVariant ? displayVariant.sales_price : product.sales_price);
    const activeStock = displayVariant ? displayVariant.stock : product.stock;
    const activeLowest30 = displayVariant ? displayVariant.lowest_price_30_days : product.lowest_price_30_days;

    return (
        <div className="grid md:grid-cols-2 gap-10">
            {/* Gallery */}
            <div className="space-y-3">
                <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center border border-slate-100 shadow-sm relative">
                    {selectedMedia ? (
                        selectedMedia.media_type === 'video' ? (
                            <video
                                key={selectedMedia.id}
                                src={getMediaUrl(selectedMedia.path)}
                                controls
                                className="w-full h-full object-contain bg-black"
                            />
                        ) : (
                            <Image
                                src={getMediaUrl(selectedMedia.path)}
                                alt={selectedMedia.alt_text || product.name}
                                fill
                                priority
                                fetchPriority="high"
                                sizes="(max-width: 768px) 100vw, 50vw"
                                className="object-contain hover:scale-105 transition-transform duration-500"
                            />
                        )
                    ) : (
                        <span className="text-4xl text-gray-300">{t('product.no_photo')}</span>
                    )}
                </div>

                {sortedImages.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
                        {sortedImages.map((img: any, idx: number) => (
                            <button
                                key={img.id}
                                onClick={() => setSelectedIndex(idx)}
                                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all relative ${
                                    idx === selectedIndex
                                        ? 'border-primary ring-2 ring-primary/30'
                                        : 'border-slate-200 hover:border-slate-400'
                                }`}
                            >
                                {img.media_type === 'video' ? (
                                    <div className="w-full h-full bg-gray-900 flex items-center justify-center">
                                        <Play className="w-4 h-4 text-white/70" />
                                    </div>
                                ) : (
                                    <Image
                                        src={getMediaUrl(img.path)}
                                        alt={img.alt_text || `${product.name} ${idx + 1}`}
                                        fill
                                        sizes="64px"
                                        className="object-cover"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="space-y-6">
                <div className="space-y-2">
                    <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 leading-tight">{product.name}</h1>
                    <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-slate-400 tracking-widest uppercase">
                        <span>SKU: {displayVariant?.sku || product.sku || 'N/A'}</span>
                        {(() => {
                            // Reaktywny stan: wariant może nadpisać stan produktu głównego
                            const effectiveCondition = (displayVariant?.condition) ?? product.condition;
                            if (!effectiveCondition) return null;
                            return (
                                <span className="product-condition-wrapper gap-2 items-center">
                                    <span className="text-slate-300">|</span>
                                    <span className={cn(
                                        "px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm transition-all duration-300",
                                        `product-badge-${effectiveCondition}`
                                    )}>
                                        {t(`product.condition.${effectiveCondition}`, effectiveCondition)}
                                    </span>
                                </span>
                            );
                        })()}
                    </div>
                </div>

                {!isTicket && (
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <div className="flex gap-3 items-baseline">
                            {activeSalesPrice !== null ? (
                                <>
                                    <p className="text-4xl font-bold text-red-600">
                                        {priceMode === 'netto' ? (activeSalesPrice / 1.23).toFixed(2) : activeSalesPrice.toFixed(2)} {currency}
                                    </p>
                                    <p className="text-2xl text-muted-foreground line-through decoration-slate-400">
                                        {priceMode === 'netto' ? ((product.items_regular_price_sum || activePrice) / 1.23).toFixed(2) : (product.items_regular_price_sum || activePrice).toFixed(2)} {currency}
                                    </p>
                                </>
                            ) : (
                                <p className="text-4xl font-bold text-primary">
                                    {priceMode === 'netto' ? ((activePrice || 0) / 1.23).toFixed(2) : activePrice?.toFixed(2)} {currency}
                                </p>
                            )}
                            <span className="text-xs font-semibold text-slate-400 uppercase">
                                {priceMode === 'netto' ? 'netto (+ VAT 23%)' : 'brutto'}
                            </span>
                        </div>
                        <NettoBruttoToggle size="sm" />
                    </div>
                    {product.product_type === 'bundle' && (product.savings_amount || 0) > 0 && (
                        <div className="p-3 bg-pink-50 border border-pink-200 rounded-xl text-pink-700 font-extrabold flex items-center justify-between animate-in fade-in">
                            <span className="text-xs sm:text-sm">Kupując w zestawie oszczędzasz:</span>
                            <span className="text-base sm:text-lg font-black text-pink-600">{(product.savings_amount).toFixed(2)} {currency}</span>
                        </div>
                    )}
                    {b2bTiers.length > 0 && (
                        <B2BTierList tiers={b2bTiers} taxRate={product.tax_rate ?? 23} net={priceMode === 'netto'} />
                    )}
                    {activeSalesPrice !== null && activeLowest30 !== null && (
                        <p className="text-xs text-muted-foreground italic bg-slate-50 p-2 rounded-lg border border-slate-100 inline-block">
                            {t('product.lowest_price_30')} <span className="font-bold">{activeLowest30.toFixed(2)} {currency}</span>
                        </p>
                    )}
                    <LoyaltyEarnPreview amount={activeSalesPrice ?? activePrice ?? 0} label="Otrzymasz za ten zakup" />
                </div>
                )}

                {/* Bundle Items List Block */}
                {product.product_type === 'bundle' && product.bundle_items && product.bundle_items.length > 0 && (
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                        <h3 className="font-bold text-slate-900 text-sm sm:text-base border-b border-slate-200 pb-2">
                            📦 Zawartość tego zestawu ({product.bundle_items.length} {product.bundle_items.length === 1 ? 'element' : 'elementy'}):
                        </h3>
                        <div className="space-y-3">
                            {product.bundle_items.map((bi: any, idx: number) => {
                                const itemProd = bi.item_product;
                                const itemVar = bi.item_variant;
                                const mainImg = itemProd?.images?.find((i: any) => i.is_main) || itemProd?.images?.[0];
                                const imgUrl = mainImg?.path ? getAbsoluteMediaUrl(mainImg.path) : null;
                                const itemPrice = bi.override_price ?? (itemVar?.sales_price ?? itemVar?.price ?? itemProd?.sales_price ?? itemProd?.price ?? 0);
                                return (
                                    <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-100 shadow-2xs gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden relative shrink-0 border border-slate-200 flex items-center justify-center">
                                                {imgUrl ? (
                                                    <Image src={toOptimizableSrc(imgUrl)} alt={itemProd?.name || ''} fill className="object-cover" />
                                                ) : (
                                                    <ShoppingBag className="w-4 h-4 text-slate-400" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs sm:text-sm font-bold text-slate-900 truncate">{itemProd?.name || 'Produkt'}</p>
                                                {itemVar && (
                                                    <p className="text-[11px] text-slate-500 font-medium">
                                                        {Object.entries(itemVar.attributes || {}).map(([k, v]) => `${k}: ${v}`).join(', ')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <span className="text-xs font-bold text-slate-500 mr-2">{bi.quantity || 1}x</span>
                                            <span className="text-xs sm:text-sm font-extrabold text-slate-900">{itemPrice.toFixed(2)} {currency}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}


                {/* Ticket Event Selector */}
                {isTicket && <EventSelector product={product} />}

                {/* Configurable Product Form */}
                {isConfigurable && (
                    <ProductVariantsBlock block={{ id: 'details-configurator', type: 'product-variants' }} product={product} />
                )}

                {/* B2B Quick Order Matrix or B2C Variant Selection */}
                {!isTicket && isVariable && (
                    isB2B ? (
                        <div className="py-2">
                            <QuickOrderMatrix product={product} />
                        </div>
                    ) : (
                        Object.keys(attributesMap).length > 0 && (
                            <div className="space-y-5 border-y border-slate-100 py-6">
                                {Object.entries(attributesMap).map(([attrName, values]) => (
                                    <div key={attrName} className="space-y-3">
                                        <Label className="text-sm font-bold text-slate-900 uppercase tracking-wider">{attrName}</Label>
                                        <div className="flex flex-wrap gap-2">
                                            {Array.from(values).map(val => (
                                                <button
                                                    key={val}
                                                    onClick={() => handleAttributeSelect(attrName, val)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                                                        selectedAttributes[attrName] === val
                                                            ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/20'
                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                                                    }`}
                                                >
                                                    {val}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )
                )}

                <div 
                    className="prose prose-slate max-w-none text-slate-600
                        prose-h1:text-[1.5em] prose-h1:font-bold prose-h1:mb-[0.1em] prose-h1:mt-0 prose-h1:text-inherit
                        prose-h2:text-[1.25em] prose-h2:font-bold prose-h2:mb-[0.1em] prose-h2:mt-0 prose-h2:text-inherit
                        prose-h3:text-[1.1em] prose-h3:font-bold prose-h3:mb-[0.1em] prose-h3:mt-0 prose-h3:text-inherit
                        prose-p:mb-[0.1em] prose-p:mt-0 prose-p:text-inherit
                        prose-a:text-blue-600 prose-a:underline
                        prose-ul:list-disc prose-ul:pl-5 prose-ul:mb-[0.1em] prose-ul:mt-0 prose-ul:text-inherit
                        prose-ol:list-decimal prose-ol:pl-5 prose-ol:mb-[0.1em] prose-ol:mt-0 prose-ol:text-inherit
                        prose-li:mb-0 prose-li:text-inherit"
                    dangerouslySetInnerHTML={{ __html: localizeHtml(safeSanitize(product.description || '')) }}
                />

                <ProductAttachmentsBlock attachments={attachments} selectedVariantId={displayVariant?.id} />

                {!isTicket && !isConfigurable && !(isB2B && isVariable) && (
                <form
                    toolname="add_to_cart"
                    tooldescription="Dodanie wybranego wariantu i ilości produktu do koszyka klienta"
                    data-webmcp-form-name="add_to_cart"
                    data-webmcp-tool-name="add_to_cart"
                    data-webmcp-description="Dodanie wybranego wariantu i ilości produktu do koszyka klienta"
                    onSubmit={(e) => e.preventDefault()}
                    className="pt-6 space-y-4"
                >
                    {!product.is_one_off && (activeStock === null || activeStock > 1) && (
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-bold text-slate-700">Liczba sztuk:</span>
                            <div className="flex items-center border border-slate-300 rounded-xl overflow-hidden bg-white shadow-xs">
                                <button
                                    type="button"
                                    onClick={() => setSelectedQuantity(prev => Math.max(1, prev - 1))}
                                    disabled={selectedQuantity <= 1}
                                    className="px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-40 font-bold transition-colors"
                                >
                                    -
                                </button>
                                <span className="px-4 py-1 text-sm font-extrabold text-slate-900 min-w-[2.5rem] text-center select-none">
                                    {selectedQuantity}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setSelectedQuantity(prev => (activeStock !== null ? Math.min(activeStock, prev + 1) : prev + 1))}
                                    disabled={activeStock !== null && selectedQuantity >= activeStock}
                                    className="px-3 py-1.5 text-slate-700 hover:bg-slate-100 disabled:opacity-40 font-bold transition-colors"
                                >
                                    +
                                </button>
                            </div>
                        </div>
                    )}
                    {!isSubscriptionPlan && (
                        <>
                            <AddToCartButton product={product} variant={currentVariant} quantity={selectedQuantity} />
                            <LeaseNowProductBadge product={product} variant={currentVariant} quantity={selectedQuantity} />
                        </>
                    )}
                    {isSubscriptionPlan && <SubscriptionPurchasePanel product={product} />}
                    {!isSubscriptionPlan && subscriptionTiers.length > 0 && (
                        <div className="pt-4 mt-2 border-t border-slate-100 space-y-2">
                            <p className="text-sm font-semibold text-slate-700">
                                {t('product.subscription_or_subscribe', 'Albo zasubskrybuj i zaoszczędź')}
                            </p>
                            <SubscriptionPurchasePanel product={product} />
                        </div>
                    )}

                    {activeStock !== null && (
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${activeStock > 0 ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
                            {activeStock > 0 ? (
                                <p className="text-sm text-green-700 font-semibold underline decoration-green-200 decoration-4 underline-offset-4">{t('product.in_stock').replace('{n}', String(activeStock))}</p>
                            ) : (
                                <p className="text-sm text-red-600 font-bold">{t('product.out_of_stock')}</p>
                            )}
                        </div>
                    )}
                </form>
                )}
            </div>
        </div>
    );
}
